import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { ChevronRight } from "lucide-react";

export default function Forum() {
  const { t, i18n } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/forum").then((r) => setGroups(r.data)).finally(() => setLoading(false));
  }, []);

  const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString(i18n.language === "no" ? "nb-NO" : i18n.language) : "0");
  const catName = (c) => i18n.exists(`forum.category.${c.slug}`) ? t(`forum.category.${c.slug}`) : c.name;
  const catDesc = (c) => i18n.exists(`forum.category_desc.${c.slug}`) ? t(`forum.category_desc.${c.slug}`) : c.description;
  const subName = (f) => i18n.exists(`forum.sub.${f.slug}`) ? t(`forum.sub.${f.slug}`) : f.name;
  const subDesc = (f) => i18n.exists(`forum.sub_desc.${f.slug}`) ? t(`forum.sub_desc.${f.slug}`) : f.description;

  const goToForum = (id) => navigate(`/forum/${id}`);
  const onRowKey = (e, id) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToForum(id); }
  };

  return (
    <Layout>
      <div className="viking-band relative">
      <div className="mb-8 sm:mb-12 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-meta mb-3">{t("forum.forum_header")}</div>
          <h1 className="font-fraunces text-3xl sm:text-4xl lg:text-5xl text-[var(--cream)] leading-tight mb-3 font-medium">
            {t("forum.page_heading")}.
          </h1>
          <p className="font-manrope text-[var(--cream)]/85 text-sm max-w-xl leading-relaxed">
            {t("forum.page_subtitle")}
          </p>
        </div>
        <Link to="/forum/mine" className="btn-ghost shrink-0" data-testid="goto-mine">
          {t("forum.my_threads")} →
        </Link>
      </div>

      {loading && <div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div>}

      <div className="space-y-10" data-testid="forum-index">
        {groups.map(({ category, forums }) => (
          <section
            key={category.id}
            className="border border-white/10 rounded-sm overflow-hidden"
            style={{ background: "rgba(255, 255, 255, 0.025)" }}
            data-testid={`category-${category.slug}`}
          >
            {/* Kategori-overskrift – mørkere grå transparent */}
            <header
              className="flex items-end justify-between gap-4 px-3 sm:px-6 py-3 sm:py-6 border-b border-white/10"
              style={{
                borderLeft: `4px solid var(--cognac)`,
                background: "rgba(255, 255, 255, 0.045)",
              }}
            >
              <div className="flex-1 min-w-0">
                <h2 className="font-fraunces text-xl sm:text-3xl text-[var(--cream)] leading-none font-semibold">
                  {catName(category)}
                </h2>
                {category.description && (
                  <div className="font-manrope text-xs text-[var(--cream)]/80 mt-2 hidden sm:block">
                    {catDesc(category)}
                  </div>
                )}
              </div>
              <div className="font-meta text-[10px] text-[var(--cream-soft)] hidden sm:block whitespace-nowrap">
                {forums.length} {t("forum.threads").toLowerCase()}
              </div>
            </header>

            {/* Kolonne-headere */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-2 border-b border-white/5">
              <div className="col-span-6 font-meta">{t("forum.forum_header")}</div>
              <div className="col-span-2 font-meta text-center">{t("forum.threads_header")}</div>
              <div className="col-span-4 font-meta">{t("forum.last_header")}</div>
            </div>

            {/* Forum-rader – lysere grå transparent */}
            <ul className="divide-y divide-white/5">
              {forums.map((f, idx) => {
                const isUnread = !!f.has_unread;
                return (
                  <li
                    key={f.id}
                    className="relative"
                    style={{ background: idx % 2 === 1 ? "rgba(255, 255, 255, 0.015)" : "rgba(255, 255, 255, 0.025)" }}
                  >
                    {/* Vertikal status-stolpe */}
                    <span
                      aria-hidden
                      className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 transition-colors ${
                        isUnread ? "" : "bg-white/10"
                      }`}
                      style={isUnread ? { background: "var(--cognac)" } : undefined}
                      data-testid={`forum-status-${f.id}-${isUnread ? "unread" : "read"}`}
                    />
                    <div
                      role="link"
                      tabIndex={0}
                      onClick={() => goToForum(f.id)}
                      onKeyDown={(e) => onRowKey(e, f.id)}
                      aria-label={`${f.name}${isUnread ? `, ${f.unread_count} uleste tråder` : ""}`}
                      className="group grid grid-cols-12 gap-2 sm:gap-4 items-center pl-3 pr-3 sm:pl-7 sm:pr-6 py-3 sm:py-5 hover:bg-white/[0.03] transition-colors cursor-pointer focus:outline-none focus-visible:bg-white/[0.05]"
                      data-testid={`forum-${f.id}`}
                    >
                      {/* navn + beskrivelse */}
                      <div className="col-span-12 md:col-span-6 min-w-0">
                        <div className={`font-fraunces italic text-base sm:text-lg leading-tight transition-colors ${
                          isUnread
                            ? "text-[var(--cream)] group-hover:text-[var(--magenta)]"
                            : "text-[var(--cream)]/85 group-hover:text-[var(--magenta)]"
                        }`}>
                          {subName(f)}
                          {isUnread && (
                            <span className="ml-2 align-middle text-[10px] font-manrope uppercase tracking-[0.2em]" style={{ color: "var(--cognac)" }} data-testid={`unread-badge-${f.id}`}>
                              {f.unread_count} {t("common.unread")}
                            </span>
                          )}
                        </div>
                        {f.description && (
                          <div className="font-manrope text-xs text-[var(--cream)]/75 mt-1.5 leading-relaxed hidden sm:block">
                            {subDesc(f)}
                          </div>
                        )}
                      </div>

                      {/* Tråder + innlegg */}
                      <div className="hidden md:block col-span-2 text-center tabular-nums" data-testid={`forum-counts-${f.id}`}>
                        <div className="font-fraunces italic text-2xl text-[var(--cream)] leading-none">
                          {fmtNum(f.thread_count)}
                        </div>
                        <div className="font-meta mt-1">{t("forum.threads").toLowerCase()}</div>
                        <div className="font-manrope text-[11px] text-[var(--cream-soft)] mt-2">
                          {fmtNum(f.post_count)} {t("forum.posts").toLowerCase()}
                        </div>
                      </div>

                      {/* Siste tråd */}
                      <div className="hidden md:flex col-span-4 items-center justify-end gap-3">
                        <div className="text-right min-w-0 flex-1">
                          {f.last_post ? (
                            <>
                              <Link
                                to={`/thread/${f.last_post.thread_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-fraunces italic text-sm text-[var(--cream)] truncate block hover:text-[var(--magenta)] transition-colors"
                                data-testid={`forum-last-${f.id}`}
                              >
                                {f.last_post.title}
                              </Link>
                              <div className="font-meta mt-0.5">
                                {t("common.by")} <span className="text-[var(--cream-soft)]">{f.last_post.username}</span>
                                {" · "}{formatRelative(f.last_post.at)}
                              </div>
                            </>
                          ) : (
                            <div className="font-meta italic text-[var(--cream-soft)]">{t("forum.no_posts_yet")}</div>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-[var(--cream-faint)] group-hover:text-[var(--magenta)] group-hover:translate-x-0.5 transition-all" />
                      </div>

                      {/* Mobil-statuslinje */}
                      <div className="md:hidden col-span-12 font-meta flex items-center gap-3 -mt-1 text-[var(--cream-soft)]">
                        <span>{t("forum.threads_mobile_stats", { threads: fmtNum(f.thread_count), posts: fmtNum(f.post_count) })}</span>
                        {isUnread && (
                          <span className="text-[var(--magenta)] uppercase tracking-[0.2em]" data-testid={`unread-badge-mobile-${f.id}`}>
                            {f.unread_count} {t("common.unread")}
                          </span>
                        )}
                        {f.last_post && (
                          <span className="truncate">· {formatRelative(f.last_post.at)}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
      </div>
    </Layout>
  );
}
